// src/components/TeslimFormu.js

import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider } from '@mui/material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import logo from '../assets/logo.png'; // Logo importu emin olalım

export const TeslimFormu = React.forwardRef(({ workOrder, ...props }, ref) => {
    if (!workOrder) { return <Box ref={ref} {...props}>Form verisi yükleniyor...</Box>; }

    // Tarihleri formatlama fonksiyonu
    const formatDate = (dateObj) => {
        if (!dateObj) return '-';
        try {
            // Firestore Timestamp kontrolü
            if (dateObj.toDate && typeof dateObj.toDate === 'function') {
                return format(dateObj.toDate(), 'dd MMMM yyyy HH:mm', { locale: tr });
            }
            // Normal Date objesi kontrolü
            if (dateObj instanceof Date) {
                return format(dateObj, 'dd MMMM yyyy HH:mm', { locale: tr });
            }
            // Eğer string valid bir tarih ise (dönüşüm deneriz)
            const d = new Date(dateObj);
            if (!isNaN(d.getTime())) {
                return format(d, 'dd MMMM yyyy HH:mm', { locale: tr });
            }
        } catch (e) {
            console.error("Tarih formatlanamadı", e);
            return '-';
        }
        return '-';
    };

    return (
        <Box
            ref={ref}
            {...props}
            sx={{
                p: 4,
                width: '100%', // 210mm yerine %100 yapalım, container (IsEmriDetay) yönetiyor zaten
                maxWidth: '210mm',
                margin: 'auto',
                bgcolor: 'white',
                color: 'black',
                boxSizing: 'border-box',
                '@media print': {
                    p: 2,
                    width: '100% !important',
                    maxWidth: 'none !important',
                    height: 'auto !important',
                    margin: '0 !important',
                    border: 'none !important'
                }
            }}
        >
            {/* Style bloğu kaldırıldı - IsEmriDetay.js içindeki @page kuralı yeterli */}

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '2px solid #000', pb: 1 }}>
                <Box>
                    {/* Logo import edilen değişken kullanıldı */}
                    <img src={logo} alt="Logo" style={{ height: 50, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>ARAÇ TESLİM FORMU</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}><strong>Tarih:</strong> {formatDate(new Date())}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}><strong>İş Emri No:</strong> {workOrder.isEmriNo || '#' + workOrder.id.substring(0, 6).toUpperCase()}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}><strong>Oluşturan:</strong> {workOrder.personelAd || 'Sistem'}</Typography>
                </Box>
            </Box>

            {/* Bilgi Kutuları */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {/* Müşteri */}
                <Box sx={{ flex: 1, border: '1px solid #000', p: 1, borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 0.5 }}>MÜŞTERİ BİLGİLERİ</Typography>
                    <Typography variant="caption" display="block"><strong>Ad Soyad:</strong> {workOrder.musteriAdSoyad}</Typography>
                    <Typography variant="caption" display="block"><strong>Telefon:</strong> {workOrder.musteriTelefon}</Typography>
                </Box>
                {/* Araç */}
                <Box sx={{ flex: 1, border: '1px solid #000', p: 1, borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 0.5 }}>ARAÇ BİLGİLERİ</Typography>
                    <Typography variant="caption" display="block"><strong>Plaka:</strong> {workOrder.aracPlaka}</Typography>
                    <Typography variant="caption" display="block"><strong>Marka/Model:</strong> {workOrder.aracMarkaModel}</Typography>
                    <Typography variant="caption" display="block"><strong>KM:</strong> {workOrder.km || '-'}</Typography>
                </Box>
            </Box>

            {/* Hizmet Tablosu */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>UYGULANAN İŞLEMLER</Typography>
                <TableContainer sx={{ border: '1px solid #000' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#eee' }}>
                                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #000', py: 0.5 }}>Hizmet Türü</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #000', py: 0.5 }}>Açıklama / Detay</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>Tutar</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {workOrder.uygulamalar?.paketler?.map((paket, idx) => (
                                <React.Fragment key={`paket-${idx}`}>
                                    <TableRow sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5 } }}>
                                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>PAKET</TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #ccc' }}>{paket.urunAdi}</TableCell>
                                        <TableCell align="right">{(paket.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                    </TableRow>
                                    {paket.icindekiHizmetler && paket.icindekiHizmetler.map((sub, sIdx) => (
                                        <TableRow key={`sub-${sIdx}`} sx={{ '& td': { borderBottom: '1px solid #eee', py: 0.3, bgcolor: '#f9f9f9', fontSize: '0.85rem', color: '#555' } }}>
                                            <TableCell sx={{ pl: 4, borderRight: '1px solid #ccc' }}> ↳ {sub.tip}</TableCell>
                                            <TableCell sx={{ borderRight: '1px solid #ccc' }}>{sub.hizmetAdi}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>Dahil</TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}

                            {['camFilmi', 'ppf', 'seramik'].map(kategori => {
                                const label = { camFilmi: 'Cam Filmi', ppf: 'PPF Kaplama', seramik: 'Seramik Kaplama' }[kategori];
                                return workOrder.uygulamalar?.[kategori]?.map((item, idx) => {
                                    if (item.satisTipi === 'paket') {
                                        return (
                                            <React.Fragment key={`${kategori}-paket-${idx}`}>
                                                <TableRow sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5, bgcolor: '#f5f5f5' } }}>
                                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{label} Paketi</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{item.paketAdi}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(item.paketFiyat || item.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                                </TableRow>
                                                {(item.parcalar || []).filter(p => p.urunId).map((parca, pi) => (
                                                    <TableRow key={`${kategori}-parca-${pi}`} sx={{ '& td': { borderBottom: '1px solid #eee', py: 0.3, bgcolor: '#f9f9f9', fontSize: '0.85rem', color: '#555' } }}>
                                                        <TableCell sx={{ pl: 4, borderRight: '1px solid #ccc' }}>↳ {parca.ad}</TableCell>
                                                        <TableCell sx={{ borderRight: '1px solid #ccc' }}>{parca.urunAdi}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{(parca.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <TableRow key={`${kategori}-${idx}`} sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5 } }}>
                                            <TableCell sx={{ borderRight: '1px solid #ccc' }}>{label}</TableCell>
                                            <TableCell sx={{ borderRight: '1px solid #ccc' }}>{item.urunAdi} ({item.ad || item.uygulamaAlani})</TableCell>
                                            <TableCell align="right">{(item.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                        </TableRow>
                                    );
                                });
                            })}

                            {workOrder.uygulamalar?.gocuk?.aktif && (
                                <TableRow sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5 } }}>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>Göçük Düzeltme</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>{workOrder.uygulamalar.gocuk.aciklama || 'Göçük Düzeltme'}</TableCell>
                                    <TableCell align="right">{(workOrder.uygulamalar.gocuk.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                </TableRow>
                            )}

                            {workOrder.uygulamalar?.yikama?.aktif && (
                                <TableRow sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5 } }}>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>Yıkama</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>Standart Yıkama</TableCell>
                                    <TableCell align="right">{(workOrder.uygulamalar.yikama.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                </TableRow>
                            )}

                            {workOrder.uygulamalar?.diger?.filter(h => h.aciklama).map((h, i) => (
                                <TableRow key={`diger-${i}`} sx={{ '& td': { borderBottom: '1px solid #ccc', py: 0.5 } }}>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>Diğer</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>{h.aciklama}</TableCell>
                                    <TableCell align="right">{(h.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                </TableRow>
                            ))}

                            {/* Toplamlar Bölümü */}
                            <TableRow>
                                <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #000', py: 1 }}>TOPLAM TUTAR (KDV hariç):</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #000', py: 1 }}>{(workOrder.toplamTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>ÖDENEN:</TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>{(workOrder.odenenTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', py: 0.5, color: '#d32f2f' }}>KALAN:</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5, color: '#d32f2f' }}>{(workOrder.kalanTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Tarih ve Notlar */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 120, border: '1px solid #ccc', p: 1, height: '80px' }}>
                    <Typography variant="caption" fontWeight="bold" display="block">ARAÇ TESLİM ALIM</Typography>
                    <Typography variant="body2">{formatDate(workOrder.isEmriTarihi)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 120, border: '1px solid #ccc', p: 1, height: '80px' }}>
                    <Typography variant="caption" fontWeight="bold" display="block">ARAÇ TESLİM EDİLME</Typography>
                    <Typography variant="body2">{formatDate(workOrder.teslimEdilmeTarihi || workOrder.teslimTarihi)}</Typography>
                </Box>
                <Box sx={{ flex: 3, minWidth: 200, border: '1px solid #ccc', p: 1, height: '80px' }}>
                    <Typography variant="caption" fontWeight="bold" display="block">NOTLAR</Typography>
                    <Typography variant="caption">{workOrder.isEmriNotlari}</Typography>
                </Box>
            </Box>

            {/* İmza Alanı */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, px: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" display="block">TESLİM EDEN</Typography>
                    <Typography variant="body2" fontWeight="bold">Solar Gard Yetkilisi</Typography>
                    <Box sx={{ height: 60, borderBottom: '1px dashed #000', mt: 1, width: 150 }} className="signature-box" />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" display="block">TESLİM ALAN</Typography>
                    <Typography variant="body2" fontWeight="bold">{workOrder.musteriAdSoyad}</Typography>
                    <Box sx={{ height: 60, borderBottom: '1px dashed #000', mt: 1, width: 150 }} className="signature-box" />
                </Box>
            </Box>

            {/* Footer / Yasal Uyarı */}
            <Box sx={{ mt: 'auto', pt: 2, textAlign: 'center', fontSize: '10px', color: '#666' }}>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    * Formdaki tüm tutarlar KDV hariçtir.
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    * Aracın durumu, hava şartları teslimat tarihini değiştirebilir.
                </Typography>
                <Typography variant="caption">
                    * Müşteri aracı teslim aldığında yapılan işlemleri kontrol ederek eksiksiz teslim aldığını beyan eder.
                    Fatura yerine geçmez. Bilgilendirme amaçlıdır.
                </Typography>
            </Box>
        </Box>
    );
});